import { useQuery } from '@tanstack/react-query';

import InclusionsService from '~/services/InclusionsService';

import { isRecord } from '~/utils/isRecord';

import { inclusionsQueryKeys } from './inclusionsQueryKeys';

export interface PackageInclusionRecord {
  id?: string | number;
  inclusion_id?: string | number;
  inclusion: {
    category_id?: string | null;
    description?: string | null;
    id: string;
    id_salesforce_external?: string | number | null;
    name: string;
    currency?: string;
    is_hidden?: boolean;
    requires_booking?: boolean;
    rrp?: number | null;
    total_value?: number | null;
    type?: string;
    unit?: string;
    category?: {
      name?: string;
    };
  };
  opportunity_name?: string;
  sort_order?: number;
  style?: string | null;
  lux_plus_tier?: string | null;
}

export type PackageInclusionsByPackageId = Record<string, Array<PackageInclusionRecord>>;

interface UseGetPackageInclusionsByOfferParams {
  offerId?: string;
  enabled?: boolean;
}

function isPackageInclusionRecord(value: unknown): value is PackageInclusionRecord {
  return isRecord(value) && isRecord(value.inclusion);
}

function normalisePackageInclusions(value: unknown): PackageInclusionsByPackageId {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<PackageInclusionsByPackageId>((packageInclusions, [packageId, inclusions]) => {
    packageInclusions[packageId] = Array.isArray(inclusions) ? inclusions.filter(isPackageInclusionRecord) : [];

    return packageInclusions;
  }, {});
}

export function useGetPackageInclusionsByOffer({ offerId, enabled = true }: UseGetPackageInclusionsByOfferParams) {
  return useQuery({
    queryKey: inclusionsQueryKeys.packageInclusionsByOffer(offerId ?? ''),
    queryFn: async () => {
      if (!offerId) {
        return {};
      }

      const response = await InclusionsService.fetchPackageInclusions(offerId);

      return normalisePackageInclusions(response.result);
    },
    enabled: enabled && !!offerId,
  });
}
